/*
 * File: authentication.service.ts
 * Project: rast-app
 * Create Date: Friday, 1st August 2025
 * Author: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Last Modified: Tuesday, 4th August 2026 11:06:58 am
 * Modified by: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Copyright (c) 2026 CPA Wernher von Braun
 * ----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	---------------------------------------------------------
 */

import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { ApiErrorResponseDto, AuthStoredData, GoogleLoginOutcome, GoogleSignUpRequestDto, LoginResponseDto, SignUpRequestDto, SignUpResponseDto, TokenData } from 'src/app/types/authentication.types';
import { ApiAuthenticationService } from '../../api/authentication/authentication.service';
import { ToastService } from '../toast/toast.service';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MonitoringService } from '../monitoring/monitoring.service';
import { BiometricAuthService } from '../biometric-auth/biometric-auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private tokenData: TokenData | null = null;

  constructor(
    private readonly authenticationService: ApiAuthenticationService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly jwtService: JwtHelperService,
    private readonly monitoringService: MonitoringService,
    private readonly biometricAuthService: BiometricAuthService
  ) { }

  async signUpUser(body: SignUpRequestDto): Promise<SignUpResponseDto> {
    await this.monitoringService.log('[Auth] Iniciando cadastro de usuário');
    try {
      const response = await lastValueFrom(this.authenticationService.signUpUser(body));
      await this.monitoringService.log('[Auth] Cadastro concluído com sucesso');
      return response;
    } catch (error: any) {
      await this.monitoringService.log(`[Auth] Erro no cadastro: ${error?.message || 'Erro desconhecido'}`);
      await this.monitoringService.recordError(error);
      this.toastService.openErrorToast(error.error.message);
      throw error;
    }
  }

  async loginUser(user: string, password: string) {
    await this.monitoringService.log('[Auth] Iniciando login');

    try {
      const loginResponse = await lastValueFrom(this.authenticationService.login(btoa(`${user}:${password}`)));
      this.storeLoginResponse(loginResponse);
      this.decodeToken();
      this.toastService.openSuccessToast('Login realizado com sucesso!');
      await this.monitoringService.log('[Auth] Login realizado com sucesso');

      await this.router.navigateByUrl('tabs/home', {
        replaceUrl: true,
        state: { reloadType: 'full' }
      });

      try {
        await this.biometricAuthService.promptToEnableIfEligible(user);
      } catch {
        // Oferta de biometria nunca deve interromper um login bem-sucedido.
      }
    } catch (error: any) {
      await this.monitoringService.log(`[Auth] Erro no login: ${error?.message || 'Erro desconhecido'}`);
      await this.monitoringService.recordError(error);
      this.toastService.openErrorToast(error.error.message);
    }
  }

  /**
   * Realiza o desbloqueio via biometria: lê o refresh token guardado pelo
   * BiometricAuthService, chama o /refresh existente e navega para
   * tabs/home, replicando a cauda de sucesso de loginUser().
   *
   * @returns false silenciosamente em qualquer falha (cancelamento,
   * dispositivo inseguro, sem token salvo) para que a tela de login caia
   * de volta no formulário normal.
   */
  async loginWithBiometrics(): Promise<boolean> {
    try {
      if (!(await this.biometricAuthService.isAvailable())) {
        await this.biometricAuthService.disable();
        return false;
      }

      await this.biometricAuthService.authenticate('Autentique-se para entrar no RAST');

      const storedRefreshToken = await this.biometricAuthService.getStoredRefreshToken();
      if (!storedRefreshToken) {
        await this.biometricAuthService.disable();
        return false;
      }

      await this.refreshToken(storedRefreshToken);
      this.toastService.openSuccessToast('Login realizado com sucesso!');
      await this.monitoringService.log('[Auth] Login por biometria realizado com sucesso');

      await this.router.navigateByUrl('tabs/home', {
        replaceUrl: true,
        state: { reloadType: 'full' }
      });

      return true;
    } catch (error: any) {
      await this.monitoringService.log(`[Auth] Login por biometria não concluído: ${error?.message || 'cancelado'}`);
      return false;
    }
  }

  async refreshToken(refreshTokenOverride?: string): Promise<void> {
    const refreshToken = refreshTokenOverride ?? localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('Refresh token not found');
    }

    const tokens = await lastValueFrom(this.authenticationService.refreshToken(refreshToken));
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    this.decodeToken();

    await this.biometricAuthService.resyncRotatedRefreshToken(tokens.refreshToken);
  }

  private storeLoginResponse(loginResponse: LoginResponseDto) {
    localStorage.setItem('accessToken', loginResponse.accessToken);
    localStorage.setItem('refreshToken', loginResponse.refreshToken);
  }

  async logoutUser() {
    await this.monitoringService.log('[Auth] Realizando logout');

    try {
      await this.biometricAuthService.disable();
    } catch (error: any) {
      await this.monitoringService.recordError(error);
    }

    this.removeAuthStoredData();
    this.toastService.openSuccessToast('Logout realizado com sucesso!');

    await this.router.navigateByUrl('/auth/login');
  }

  removeAuthStoredData() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    this.tokenData = null;
  }

  isUserLoggedIn(): boolean {
    return localStorage.getItem('accessToken') !== null && localStorage.getItem('refreshToken') !== null;
  }

  get authStoredData(): AuthStoredData | null {
    if (this.isUserLoggedIn())
      return {
        accessToken: localStorage.getItem('accessToken')!,
        refreshToken: localStorage.getItem('refreshToken')!,
      };

    return null;
  }

  /**
   * Method to get decoded data from access token from
   * local storage. If there is none accessToken on localStorage
   * return null
   *
   * @returns null | TokenData
   */
  get tokenDecodedData(): TokenData | null {
    if (this.tokenData !== null) return this.tokenData;

    this.decodeToken();

    return this.tokenData;
  }

  decodeToken() {
    if (!this.isUserLoggedIn()) return;

    const jwtToken = localStorage.getItem('accessToken');

    this.tokenData = this.jwtService.decodeToken(jwtToken!);
  }

  async loginGoogle(body: GoogleSignUpRequestDto): Promise<GoogleLoginOutcome> {
    try {
      const data = await lastValueFrom(this.authenticationService.loginGoogle(body));
      return { status: 'success', data };
    } catch (error: any) {
      const apiError: ApiErrorResponseDto | undefined = error instanceof HttpErrorResponse ? error.error : undefined;

      if (error instanceof HttpErrorResponse && error.status === 404 && apiError?.code === 3000) {
        return { status: 'cpfRequired' };
      }

      await this.monitoringService.recordError(error);

      return {
        status: 'error',
        code: apiError?.code,
        message: apiError?.message || 'Falha ao verificar credenciais com o Google.',
      };
    }
  }

  async finalizeGoogleLogin(loginResponse: LoginResponseDto): Promise<void> {
    this.storeLoginResponse(loginResponse);
    this.decodeToken();

    await this.monitoringService.log('[AuthService] Login via Google realizado com sucesso');
    this.toastService.openSuccessToast('Login realizado com sucesso!');

    await this.router.navigateByUrl('tabs/home', {
      replaceUrl: true,
      state: { reloadType: 'full' },
    });

    try {
      const username = this.tokenDecodedData?.username;
      if (username) {
        await this.biometricAuthService.promptToEnableIfEligible(username);
      }
    } catch {
      // Oferta de biometria nunca deve interromper um login bem-sucedido.
    }
  }
}
